/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import { IActions } from '../../../interfaces/actions'
import { IGetMaxValueProps, IGetMaxValueResult } from './Counter.types'

/**
 * Round number to next decimal step
 * Consider that 0.5 is the middle of the step, so it will round to the upper step.
 * For other cases, it will round to the nearest step.
 * @param number number to round
 * @returns number, rounded number
 */
export const roundNumberToNextDecimalStep = (number: number): number => {
  const numberToString = number.toString()
  let decimal = number - parseInt(numberToString, 10)
  decimal = Math.round(decimal * 10)
  if (decimal === 5 || decimal === 0) return number
  if (decimal <= 3 || decimal >= 7) {
    return Math.round(number)
  }
  return parseInt(numberToString, 10) + 0.5
}

/**
 * Execute actions if they are defined and not disabled.
 * If disabled is true, it will execute disabledActions if defined.
 */
export const executeCounterActions = (args: {
  value: number
  newValue: number
  resultFixed?: boolean
  step: number
  data?: any
  disabled: boolean
  actions?: IActions
  disabledActions?: IActions
  adjustQuantity?: IActions
  setValue: React.Dispatch<React.SetStateAction<number>>
}) => {
  const {
    disabled,
    setValue,
    actions,
    disabledActions,
    resultFixed = false,
    adjustQuantity,
    ...rest
  } = args
  if (disabled) {
    if (disabledActions) {
      disabledActions({
        resultFixed,
        ...rest
      })
    }
    return
  }
  if (actions) {
    const maybeAdjustedValue = adjustQuantity?.({
      resultFixed,
      ...rest
    })
    const adjustedValue = typeof maybeAdjustedValue === 'number'? maybeAdjustedValue: rest.newValue

    setValue(adjustedValue)
    actions({
      ...rest,
      resultFixed,
      newValue: adjustedValue,
    })
  }
}

/**
 * Check if counter has to be disabled.
 * If validate is 'min', it will check if value is less than min.
 * If validate is 'max', it will check if value is greater than max or stock.
 * If validate is not defined, it will check both.
 */
export const isDisabled = (args: {
  value: number
  step?: number
  min?: number
  max?: number
  stock?: number | null
  validate?: 'min' | 'max'
}): boolean => {
  const { value, step = 0, min = 0, max, stock, validate = 'both' } = args
  const minDisabled = value - step < min
  const lowerLimitForMaxDisabled =
    (!!max && !!stock && Math.min(max, stock)) || max || stock
  const maxDisabled =
    !!lowerLimitForMaxDisabled && value + step > lowerLimitForMaxDisabled
  if (validate === 'min') {
    return minDisabled
  }
  if (validate === 'max') {
    return maxDisabled
  }
  return minDisabled || maxDisabled
}

export const getMaxValue = ({
  allowSetMaxvalue,
  initialValue,
  newValue,
  stock,
  max,
  oldValue
}: IGetMaxValueProps): IGetMaxValueResult => {
  let resultValue = newValue
  let resultFixed = false
  const maxValue = Math.max(stock || 0, max || 0)
  if (
    allowSetMaxvalue &&
    oldValue === initialValue &&
    initialValue !== newValue &&
    newValue > maxValue &&
    maxValue > 0
  ) {
    resultValue = maxValue
    resultFixed = true
  }
  return { resultValue, resultFixed }
}
